import React from "react";
import { Link } from "react-router-dom";
import { OmnisightMark } from "@app/shared/src/components_v2";

/**
 * The frame around everything a person sees before they are inside the console: sign-in, the
 * password reset request, account creation, the terms, and the welcome page a user with no
 * organization lands on.
 *
 * The product mark on top and the terms in the footer, so the legal link is reachable from every
 * one of these pages and not only from the console's own footer. `wide` is for pages that carry a
 * document or a two-column form rather than a single stack of fields.
 */
export const AuthFrame: React.FC<{ wide?: boolean; children: React.ReactNode }> = ({ wide, children }) => (
    <div className="flex min-h-full flex-col items-center bg-[var(--app)] p-6 text-[var(--text)]">
        <div className={`flex w-full flex-1 flex-col justify-center ${wide ? "max-w-[760px]" : "max-w-[380px]"}`}>
            <div className="mb-6 flex items-center gap-3">
                <OmnisightMark size={34} />
                <div>
                    <div className="text-[20px] font-bold tracking-tight">Fleet Commander</div>
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--faint)]">by Omnisight</div>
                </div>
            </div>
            {children}
        </div>
        <footer className="mt-8 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[10.5px] leading-relaxed text-[var(--faint)]">
            <span>Omnisight Fleet Commander · © Omnisight, Inc.</span>
            <Link to="/terms" className="font-semibold text-[var(--muted)] hover:text-[var(--text)]">
                Terms and Conditions
            </Link>
        </footer>
    </div>
);

/** A field label in the sign-in family's uppercase style. */
export const AuthLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--faint)]">{children}</label>
);

/** The panel the fields sit in. */
export const AuthPanel: React.FC<React.FormHTMLAttributes<HTMLFormElement>> = ({ className = "", children, ...rest }) => (
    <form {...rest} className={`rounded-lg border border-[var(--line)] bg-[var(--surface)] p-5 ${className}`}>
        {children}
    </form>
);

/** The quiet text links under a panel — "Forgot your password?" and its neighbours. */
export const AuthLinks: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[12.5px] font-semibold text-[var(--muted)]">
        {children}
    </div>
);
